import { useState, useMemo, type FormEvent } from "react";
import { customersViewCopy } from "../app/i18n";
import type { AppLanguage } from "../app/language";
import { AnimatedModal } from "../app/ui";
import { formatCount, getCustomerSeatCount } from "../app/utils";
import type { Customer, CustomerCreateInput, Ticket } from "../app/types";

type CustomerHealthFilter = "All" | Customer["health"];

type CustomerSummary = {
  customer: Customer;
  tickets: Ticket[];
  openTickets: number;
  resolvedTickets: number;
  highPriorityOpen: number;
};

type CustomersViewProps = {
  customers: Customer[];
  tickets: Ticket[];
  language: AppLanguage;
  canEditCustomers?: boolean;
  isLoadingCustomers?: boolean;
  onOpenCustomer: (customerId: string) => void;
  onCreateCustomer: (input: CustomerCreateInput) => void;
  onDeleteCustomer: (customerId: string) => void;
};

const emptyCreateCustomerDraft: CustomerCreateInput = {
  company: "",
  primaryContactName: "",
  email: "",
  phone: "",
  region: "",
  health: "Healthy",
};

const healthNameClasses: Record<Customer["health"], string> = {
  Healthy: "bg-[linear-gradient(135deg,#006949,#00855d)] text-white",
  Watch: "bg-[linear-gradient(135deg,#b54708,#f38744)] text-white",
  Escalated: "bg-[linear-gradient(135deg,#b42318,#f97066)] text-white",
  Disabled: "bg-[linear-gradient(135deg,#475467,#667085)] text-white",
};

const customerHealthFilters = ["All", "Healthy", "Watch", "Escalated", "Disabled"] as CustomerHealthFilter[];

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function CustomerStatCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

function getDaysUntil(dateLabel: string) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateLabel.trim());
  const renewalDate = isoMatch
    ? new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]))
    : new Date(dateLabel);
  const timeDifference = renewalDate.getTime() - today.getTime();

  return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
}

export function CustomersView({
  customers,
  tickets,
  language,
  canEditCustomers = true,
  isLoadingCustomers = false,
  onOpenCustomer,
  onCreateCustomer,
  onDeleteCustomer,
}: CustomersViewProps) {
  const copy = customersViewCopy[language];
  const [healthFilter, setHealthFilter] = useState<CustomerHealthFilter>("All");
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [createCustomerDraft, setCreateCustomerDraft] = useState<CustomerCreateInput>(emptyCreateCustomerDraft);

  const customerSummaries = useMemo<CustomerSummary[]>(() => {
    return customers
      .map(customer => {
        const relatedTickets = tickets.filter(ticket => ticket.customerId === customer.id);
        const openTickets = relatedTickets.filter(ticket => ticket.status !== "Resolved").length;
        const resolvedTickets = relatedTickets.filter(ticket => ticket.status === "Resolved").length;
        const highPriorityOpen = relatedTickets.filter(ticket => ticket.priority === "High" && ticket.status !== "Resolved").length;

        return {
          customer,
          tickets: relatedTickets,
          openTickets,
          resolvedTickets,
          highPriorityOpen,
        };
      })
      .sort((left, right) => {
        if (left.customer.health !== right.customer.health) {
          const order = { Escalated: 0, Watch: 1, Healthy: 2, Disabled: 3 } as const;
          return order[left.customer.health] - order[right.customer.health];
        }

        if (left.highPriorityOpen !== right.highPriorityOpen) {
          return right.highPriorityOpen - left.highPriorityOpen;
        }

        return left.customer.name.localeCompare(right.customer.name);
      });
  }, [customers, tickets]);

  const filteredCustomers = useMemo(() => {
    return customerSummaries.filter(summary => {
      const matchesHealth = healthFilter === "All" ? true : summary.customer.health === healthFilter;

      return matchesHealth;
    });
  }, [customerSummaries, healthFilter]);

  const totalOpenTickets = customerSummaries.reduce((total, summary) => total + summary.openTickets, 0);
  const atRiskCustomers = customerSummaries.filter(summary => summary.customer.health !== "Healthy" || summary.highPriorityOpen > 0).length;
  const renewalsSoon = customerSummaries.reduce(
    (total, summary) => total + summary.customer.products.filter(product => product.renewalDate && getDaysUntil(product.renewalDate) <= 90).length,
    0,
  );

  const handleCreateCustomer = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onCreateCustomer({
      ...createCustomerDraft,
      company: createCustomerDraft.company.trim(),
      primaryContactName: createCustomerDraft.primaryContactName.trim(),
      email: createCustomerDraft.email.trim(),
      phone: createCustomerDraft.phone.trim(),
      region: createCustomerDraft.region.trim(),
    });
    setIsCreateCustomerOpen(false);
    setCreateCustomerDraft(emptyCreateCustomerDraft);
  };

  return (
    <>
      <section className="space-y-8 p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
            <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
            <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.page.description}</p>
          </div>

          <div className="flex items-center gap-3">
            {canEditCustomers ? (
              <button
                type="button"
                onClick={() => setIsCreateCustomerOpen(true)}
                className="brand-primary-button flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-all"
              >
                <span>＋</span>
                <span>{copy.page.newCustomer}</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <CustomerStatCard label={copy.stats.managedAccounts} value={formatCount(customerSummaries.length)} detail={copy.stats.managedAccountsDetail} isLoading={isLoadingCustomers} />
          <CustomerStatCard label={copy.stats.openTickets} value={formatCount(totalOpenTickets)} detail={copy.stats.openTicketsDetail} isLoading={isLoadingCustomers} />
          <CustomerStatCard label={copy.stats.atRisk} value={formatCount(atRiskCustomers)} detail={copy.stats.atRiskDetail} isLoading={isLoadingCustomers} />
          <CustomerStatCard label={copy.stats.renewingSoon} value={formatCount(renewalsSoon)} detail={copy.stats.renewingSoonDetail} isLoading={isLoadingCustomers} />
        </div>

        <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.directory.title}</h2>
              <p className="mt-1 text-sm text-[#667085]">{copy.directory.description}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {customerHealthFilters.map(filter => {
                const active = filter === healthFilter;

                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setHealthFilter(filter)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                      active ? "brand-primary-solid" : "bg-[#f3f4f6] text-[#475467] hover:bg-[#e4e7ec]"
                    }`}
                  >
                    {copy.healthLabels[filter]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative min-h-[200px]">
            {isLoadingCustomers ? (
              <div className={`pointer-events-none absolute inset-x-0 z-[1] flex ${filteredCustomers.length === 0 ? "inset-y-0 items-center justify-center bg-white/75 backdrop-blur-[1px]" : "bottom-5 items-center justify-center px-5"}`}>
                <LoadingSpinnerBadge label={copy.directory.loading} />
              </div>
            ) : null}

            {filteredCustomers.length === 0 ? (
              isLoadingCustomers ? null : (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.directory.emptyTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.directory.emptyDetail}</p>
                </div>
              )
            ) : (
              <div className="space-y-5">
                {filteredCustomers.map(summary => {
                const primaryContact = summary.customer.contacts.find(contact => contact.role === "Primary") ?? summary.customer.contacts[0];
                const secondaryContact =
                  summary.customer.contacts.find(contact => contact.role === "Secondary") ?? summary.customer.contacts.find(contact => contact.id !== primaryContact?.id);

                return (
                  <div
                    key={summary.customer.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenCustomer(summary.customer.id)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onOpenCustomer(summary.customer.id);
                      }
                    }}
                    className={`rounded-[26px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-6 shadow-[0_12px_32px_rgba(15,23,42,0.04)] transition-all hover:border-[#cfd4dc] hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#006949]/15 ${summary.customer.health === "Disabled" ? "opacity-80" : ""}`}
                  >
                    <div className="block w-full text-left">
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                      <div className="order-1 min-w-0 md:order-1 lg:order-1">
                        <div className="flex flex-col gap-y-2 sm:flex-row sm:items-center sm:gap-x-4 md:flex-col md:items-start md:gap-x-0 lg:flex-row lg:items-center lg:gap-x-4">
                          <div className={`rounded-[14px] px-3 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] ${healthNameClasses[summary.customer.health]}`}>
                            <h3 className="text-xl font-semibold leading-none tracking-[-0.02em]">{summary.customer.company}</h3>
                          </div>
                          <p className="text-sm text-[#667085]">{summary.customer.region}</p>
                        </div>
                        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 pr-2 whitespace-nowrap custom-scrollbar">
                          <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.healthLabels[summary.customer.health]}</span>
                          <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">{copy.directory.seats(getCustomerSeatCount(summary.customer))}</span>
                          {summary.customer.products.map(product => (
                            <span key={product.name} className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">
                              {product.name} · {copy.directory.seats(product.seats)}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="order-2 min-w-0 px-1 py-1 md:order-2 lg:order-2">
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.directory.primaryContact}</p>
                            <p className="mt-2 text-sm font-semibold text-[#101828]">{primaryContact?.name || copy.directory.noPrimaryContact}</p>
                            <p className="mt-1 text-sm text-[#101828]">{primaryContact?.email || copy.common.dash}</p>
                            <p className="mt-1 text-sm text-[#667085]">{primaryContact?.phone || copy.common.dash}</p>
                          </div>

                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.directory.secondaryContact}</p>
                            <p className="mt-2 text-sm font-semibold text-[#101828]">{secondaryContact?.name || copy.common.dash}</p>
                            <p className="mt-1 text-sm text-[#101828]">{secondaryContact?.email || copy.common.dash}</p>
                          </div>
                        </div>
                      </div>

                      <div className="order-3 w-full max-w-[180px] px-1 py-1 md:col-span-2 lg:col-span-1 lg:justify-self-end">
                        <div className="flex items-start justify-between gap-5 text-sm whitespace-nowrap">
                          <div>
                            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.directory.open}</p>
                            <p className="mt-1 text-base font-bold text-[#101828]">{formatCount(summary.openTickets)}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.directory.resolved}</p>
                            <p className="mt-1 text-base font-bold text-[#101828]">{formatCount(summary.resolvedTickets)}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.directory.priority}</p>
                            <p className="mt-1 text-base font-bold text-[#101828]">{formatCount(summary.highPriorityOpen)}</p>
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <AnimatedModal
        open={isCreateCustomerOpen && canEditCustomers}
        onClose={() => setIsCreateCustomerOpen(false)}
        backdropAriaLabel={copy.form.closeDialog}
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 w-full max-w-3xl rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.form.eyebrow}</p>
            <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.form.title}</h2>
            <p className="mt-2 text-sm text-[#667085]">{copy.form.description}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setIsCreateCustomerOpen(false);
              setCreateCustomerDraft(emptyCreateCustomerDraft);
            }}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleCreateCustomer} className="mt-6 space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.form.company}</span>
              <input
                type="text"
                value={createCustomerDraft.company}
                onChange={event => setCreateCustomerDraft(current => ({ ...current, company: event.target.value }))}
                placeholder={copy.form.companyPlaceholder}
                required
                className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.form.primaryContact}</span>
              <input
                type="text"
                value={createCustomerDraft.primaryContactName}
                onChange={event => setCreateCustomerDraft(current => ({ ...current, primaryContactName: event.target.value }))}
                placeholder={copy.form.primaryContactPlaceholder}
                required
                className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.form.email}</span>
              <input
                type="email"
                value={createCustomerDraft.email}
                onChange={event => setCreateCustomerDraft(current => ({ ...current, email: event.target.value }))}
                placeholder={copy.form.emailPlaceholder}
                required
                className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.form.phone}</span>
              <input
                type="text"
                value={createCustomerDraft.phone}
                onChange={event => setCreateCustomerDraft(current => ({ ...current, phone: event.target.value }))}
                placeholder={copy.form.phonePlaceholder}
                className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.form.region}</span>
              <input
                type="text"
                value={createCustomerDraft.region}
                onChange={event => setCreateCustomerDraft(current => ({ ...current, region: event.target.value }))}
                placeholder={copy.form.regionPlaceholder}
                required
                className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.form.health}</span>
              <select
                value={createCustomerDraft.health}
                onChange={event => setCreateCustomerDraft(current => ({ ...current, health: event.target.value as Customer["health"] }))}
                className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              >
                {(["Healthy", "Watch", "Escalated", "Disabled"] as Customer["health"][]).map(health => (
                  <option key={health} value={health}>
                    {copy.healthLabels[health]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setIsCreateCustomerOpen(false);
                setCreateCustomerDraft(emptyCreateCustomerDraft);
              }}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
            >
              {copy.form.cancel}
            </button>
            <button
              type="submit"
              className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all"
            >
              {copy.form.createCustomer}
            </button>
          </div>
        </form>
      </AnimatedModal>
    </>
  );
}
